import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  Divider,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  TrendingUp as TrendingUpIcon,
  SmartToy as AIIcon,
  Security as SecurityIcon,
  Assessment as AssessmentIcon,
  Print as PrintIcon,
  Policy as PolicyIcon,
  Visibility as VisibilityIcon,
  VerifiedUser as VerifiedUserIcon,
  Speed as SpeedIcon,
  AutoGraph as AutoGraphIcon,
  IntegrationInstructions as IntegrationIcon,
  Psychology as PsychologyIcon,
  Shield as ShieldIcon,
  Gavel as GavelIcon,
} from '@mui/icons-material';

const AIGovernanceRoadmap: React.FC = () => {
  // Current state features
  const currentFeatures = [
    {
      category: 'Data Management',
      icon: <AssessmentIcon />,
      features: [
        'Data quality monitoring and integrity controls',
        'Master data management programs aligned with DHS standards',
        'Data reusability frameworks and standardization',
        'Automated data intake and validation',
        'Quality rule enforcement for AI-enabled systems',
      ],
      status: 'Implemented',
      color: '#4caf50',
    },
    {
      category: 'Data Culture',
      icon: <PsychologyIcon />,
      features: [
        'Data literacy training programs',
        'Documentation and knowledge management',
        'Stakeholder outreach and engagement',
        'Trust-building for algorithmic outputs',
        'Data-driven decision-making culture',
      ],
      status: 'In Progress',
      color: '#ff9800',
    },
    {
      category: 'Evidence Building',
      icon: <AutoGraphIcon />,
      features: [
        'Data science modeling and analytics',
        'Policy effectiveness monitoring',
        'Automated evaluation tools',
        'Process change guidance',
        'Performance measurement frameworks',
      ],
      status: 'Partial',
      color: '#2196f3',
    },
    {
      category: 'Process Streamlining',
      icon: <SpeedIcon />,
      features: [
        'Risk-based automated case complexity models',
        'Data consolidation and optimization',
        'Officer resource optimization',
        'Workflow automation',
        'Efficiency improvement initiatives',
      ],
      status: 'In Progress',
      color: '#ff9800',
    },
  ];

  // Unidatum-Inspired AI Governance Capabilities
  const unidatumCapabilities = [
    {
      category: 'Model Lifecycle Management',
      description: 'Register, monitor, document, and trace AI models',
      features: [
        'Universal model registry for all AI/ML platforms',
        'Automated model documentation and metadata',
        'End-to-end lineage tracking from data to deployment',
        'Model versioning and rollback capabilities',
        'Ownership and accountability tracking',
        'Lifecycle stage governance (dev to production)',
      ],
      platforms: ['Databricks', 'AWS SageMaker', 'Azure AI', 'Google Vertex AI', 'AWS Bedrock', 'E-Unify (USCIS)'],
    },
    {
      category: 'Regulatory Compliance Automation',
      description: 'Automated checks for major policies and regulations',
      features: [
        'EU AI Act compliance automation',
        'NIST AI RMF alignment and assessment',
        'OMB AI guidance compliance checks',
        'Policy alignment verification workflows',
        'Automated audit trail generation',
        'Regulatory reporting and documentation',
      ],
      platforms: ['All AI/ML Platforms'],
    },
    {
      category: 'Transparency & Auditability',
      description: 'End-to-end lineage, metadata, and audit trails',
      features: [
        'Complete model transparency and traceability',
        'Built-in AI model documentation standards',
        'Cross-platform lineage visualization',
        'Automated compliance documentation',
        'Stakeholder review and approval workflows',
        'Audit-ready reporting and evidence',
      ],
      platforms: ['All AI/ML Platforms'],
    },
    {
      category: 'Ethical AI & Risk Management',
      description: 'Bias detection, risk mitigation frameworks',
      features: [
        'Automated bias detection and monitoring',
        'Risk assessment and scoring frameworks',
        'Ethical AI principles enforcement',
        'Model drift detection and alerts',
        'Impact assessment automation',
        'Risk mitigation strategy tracking',
      ],
      platforms: ['All AI/ML Platforms'],
    },
    {
      category: 'AI Use Case Lifecycle Streamlining',
      description: 'Unified platform view with structured workflows',
      features: [
        'Single platform for AI and data teams',
        'Structured workflows from data to deployment',
        'Friction elimination in AI lifecycle',
        'Cross-functional collaboration tools',
        'Self-service AI governance resources',
        'Business context integration for models',
      ],
      platforms: ['All AI/ML Platforms'],
    },
    {
      category: 'Generative AI Support',
      description: 'Automated documentation and governance for GenAI',
      features: [
        'Auto-generated data dictionaries',
        'Automated model documentation',
        'GenAI-specific compliance checks',
        'Prompt governance and tracking',
        'LLM output monitoring and validation',
        'Reduced manual documentation effort',
      ],
      platforms: ['AWS Bedrock', 'Azure OpenAI', 'Google Vertex AI'],
    },
  ];

  // AI Governance enhancements
  const aiGovernanceEnhancements = [
    {
      category: 'Responsible AI Framework',
      priority: 'Critical',
      features: [
        'AI model inventory and lifecycle management',
        'Ethical AI principles and guidelines',
        'Bias detection and mitigation protocols',
        'Explainable AI (XAI) implementation',
        'AI risk assessment and scoring framework',
        'Model transparency and documentation standards',
      ],
      effort: 'Large',
      value: 'Very High',
      owner: 'OCDO - Advanced Analytics Branch',
      unidatumAlignment: 'Model Lifecycle Management + Ethical AI & Risk Management',
    },
    {
      category: 'Data Governance for AI',
      priority: 'Critical',
      features: [
        'AI-specific data quality standards',
        'Training data governance and versioning',
        'Data lineage for ML pipelines',
        'Automated metadata generation for AI systems',
        'Data integrity controls for model inputs',
        'Master data management for AI applications',
      ],
      effort: 'Large',
      value: 'Very High',
      owner: 'OCDO - Data Sharing and Governance Branch',
      unidatumAlignment: 'Transparency & Auditability + AI Use Case Lifecycle',
    },
    {
      category: 'AI Risk Management',
      priority: 'Critical',
      features: [
        'FICO-like risk scoring governance',
        'Automated case routing oversight',
        'Model performance monitoring and alerts',
        'Drift detection and remediation',
        'Regular model audits and reviews',
        'Risk mitigation strategies and controls',
      ],
      effort: 'Medium',
      value: 'Very High',
      owner: 'OCDO - Data Management Council',
      unidatumAlignment: 'Ethical AI & Risk Management',
    },
    {
      category: 'Compliance & Transparency',
      priority: 'High',
      features: [
        'AI decision traceability and audit trails',
        'Regulatory compliance automation (OMB, NIST, EU AI Act)',
        'Transparent reporting and dashboards',
        'Stakeholder communication protocols',
        'Documentation standards for AI systems',
        'Compliance verification workflows',
      ],
      effort: 'Medium',
      value: 'High',
      owner: 'OCDO - Data Governance Framework',
      unidatumAlignment: 'Regulatory Compliance Automation + Transparency & Auditability',
    },
    {
      category: 'Data Literacy for AI',
      priority: 'High',
      features: [
        'AI ethics training for staff',
        'Explainable model education programs',
        'Process documentation for AI workflows',
        'Best practices for AI usage',
        'Continuous learning and upskilling',
        'Cross-functional AI awareness initiatives',
      ],
      effort: 'Medium',
      value: 'High',
      owner: 'OCDO - Data Culture Team',
      unidatumAlignment: 'AI Use Case Lifecycle Streamlining',
    },
    {
      category: 'AI Operations & Monitoring',
      priority: 'High',
      features: [
        'Real-time model performance dashboards',
        'Automated anomaly detection for AI outputs',
        'Model versioning and rollback capabilities',
        'A/B testing framework for AI models',
        'Performance benchmarking and KPIs',
        'Continuous integration/deployment for ML',
      ],
      effort: 'Large',
      value: 'High',
      owner: 'OCDO - Business Intelligence Branch',
      unidatumAlignment: 'Model Lifecycle Management',
    },
    {
      category: 'Platform Integration & Standards',
      priority: 'High',
      features: [
        'Databricks AI/ML platform integration',
        'AWS SageMaker and Bedrock governance',
        'Azure AI and OpenAI integration',
        'Google Vertex AI platform support',
        'Cross-platform metadata frameworks',
        'Unified governance across tech stacks',
      ],
      effort: 'Large',
      value: 'Very High',
      owner: 'OCDO - Enterprise Architecture',
      unidatumAlignment: 'Platform Integrations (Databricks, AWS, Azure, Google)',
    },
    {
      category: 'Generative AI Governance',
      priority: 'High',
      features: [
        'LLM and GenAI model governance',
        'Prompt engineering and tracking',
        'Auto-generated documentation for AI assets',
        'GenAI-specific compliance automation',
        'Output validation and monitoring',
        'Responsible GenAI usage policies',
      ],
      effort: 'Medium',
      value: 'Very High',
      owner: 'OCDO - Advanced Analytics Branch',
      unidatumAlignment: 'Generative AI Support',
    },
  ];

  // Implementation roadmap
  const roadmapPhases = [
    {
      phase: 'Phase 1: Foundation & Framework',
      timeline: '0-3 Months',
      focus: 'Governance Structure & Policies',
      items: [
        'Establish AI Governance Council and working groups',
        'Define responsible AI principles and ethical guidelines',
        'Create AI model inventory and classification system',
        'Develop AI risk assessment framework',
        'Implement model documentation standards',
        'Launch AI literacy training program',
      ],
      milestones: [
        'AI Governance Charter approved',
        'Model inventory system operational',
        'Risk framework published',
      ],
    },
    {
      phase: 'Phase 2: Controls & Compliance',
      timeline: '3-6 Months',
      focus: 'Risk Management & Oversight',
      items: [
        'Deploy AI-specific data quality controls',
        'Implement bias detection and monitoring tools',
        'Establish model performance tracking dashboards',
        'Create audit trail and traceability systems',
        'Develop compliance automation workflows',
        'Launch explainable AI pilot programs',
      ],
      milestones: [
        'Bias detection tools deployed',
        'Performance dashboards live',
        'First AI audit completed',
      ],
    },
    {
      phase: 'Phase 3: Advanced Capabilities',
      timeline: '6-9 Months',
      focus: 'Automation & Intelligence',
      items: [
        'Implement automated model validation pipelines',
        'Deploy real-time drift detection systems',
        'Create AI-powered governance automation',
        'Establish model versioning and rollback procedures',
        'Implement A/B testing framework for models',
        'Launch advanced analytics for AI governance',
      ],
      milestones: [
        'Automated validation operational',
        'Drift detection system live',
        'A/B testing framework deployed',
      ],
    },
    {
      phase: 'Phase 4: Enterprise Integration',
      timeline: '9-12 Months',
      focus: 'Scale & Optimization',
      items: [
        'Scale governance framework across all AI systems',
        'Integrate with enterprise risk management',
        'Deploy advanced transparency and reporting tools',
        'Establish continuous improvement processes',
        'Implement federated AI governance model',
        'Launch enterprise-wide AI governance platform',
      ],
      milestones: [
        'Enterprise platform deployed',
        'All AI systems governed',
        'Continuous monitoring active',
      ],
    },
  ];

  // Key governance roles
  const governanceRoles = [
    {
      role: 'Office of Chief Data Officer (OCDO)',
      responsibility: 'Enterprise data management, governance, and AI strategy leadership',
      activities: ['Policy development', 'Framework oversight', 'Strategic planning'],
    },
    {
      role: 'Data Sharing and Governance Branch',
      responsibility: 'Data Governance Framework management and policy enforcement',
      activities: ['Policy enforcement', 'Council facilitation', 'Compliance monitoring'],
    },
    {
      role: 'Advanced Analytics Branch',
      responsibility: 'AI/ML model development, deployment, and technical evaluation',
      activities: ['Model development', 'Tech evaluation', 'Process automation'],
    },
    {
      role: 'Business Intelligence Branch',
      responsibility: 'Dashboarding, visualization, and performance monitoring',
      activities: ['Dashboard creation', 'Reporting', 'Performance tracking'],
    },
    {
      role: 'Data Management Council',
      responsibility: 'Strategic oversight of data and AI governance initiatives',
      activities: ['Strategic decisions', 'Resource allocation', 'Risk oversight'],
    },
  ];

  return (
    <>
      {/* Print Styles */}
      <style>
        {`
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            @page {
              size: letter;
              margin: 0.5in;
            }
            body {
              margin: 0 !important;
              padding: 0 !important;
            }
            /* Hide navigation elements */
            nav, .MuiDrawer-root, .MuiAppBar-root {
              display: none !important;
            }
            /* Preserve Grid layout for columns */
            .MuiGrid-container {
              display: flex !important;
              flex-wrap: wrap !important;
            }
            .MuiGrid-item {
              flex-shrink: 0 !important;
            }
            /* 4-column layout for summary cards */
            .MuiGrid-grid-xs-12.MuiGrid-grid-md-3 {
              flex: 0 0 25% !important;
              max-width: 25% !important;
            }
            /* 2-column layout for capability cards */
            .MuiGrid-grid-xs-12.MuiGrid-grid-md-6 {
              flex: 0 0 50% !important;
              max-width: 50% !important;
            }
            /* 3-column layout for benefits cards */
            .MuiGrid-grid-xs-12.MuiGrid-grid-md-4 {
              flex: 0 0 33.333% !important;
              max-width: 33.333% !important;
            }
            /* Ensure colors are preserved */
            .MuiPaper-root, .MuiCard-root, .MuiChip-root, .MuiAlert-root, .MuiButton-root {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            /* Preserve table styling */
            .MuiTable-root {
              page-break-inside: auto !important;
            }
            .MuiTableRow-root {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            /* Prevent page breaks inside cards */
            .MuiCard-root, .MuiPaper-root {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            /* Hide print button */
            .print-button {
              display: none !important;
            }
            /* Remove top spacing from main container */
            .MuiBox-root {
              margin-top: 0 !important;
            }
          }
        `}
      </style>

      <Box sx={{ width: '100%', maxWidth: '1400px', mx: 'auto', mt: 4, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ color: '#003366', fontWeight: 700 }}>
              AI Governance Roadmap for USCIS
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Responsible AI Management & Data Strategy Integration
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
            className="print-button"
            sx={{
              backgroundColor: '#003366',
              '&:hover': { backgroundColor: '#002244' },
            }}
          >
            Print to PDF
          </Button>
        </Box>

        {/* Executive Summary */}
        <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976d2' }}>
          <Typography variant="h5" gutterBottom sx={{ color: '#1976d2', fontWeight: 600 }}>
            Executive Summary
          </Typography>
          <Typography variant="body1" paragraph>
            AI governance at USCIS is deeply integrated into its data strategy, focusing on responsible management, oversight, and quality of data that increasingly includes artificial intelligence and advanced analytics solutions. Led by the Office of the Chief Data Officer (OCDO), this roadmap outlines a comprehensive approach to implementing responsible AI practices across four strategic goals: Data Management, Data Culture, Evidence Building, and Process Streamlining.
          </Typography>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%', bgcolor: '#4caf50', color: 'white' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>4</Typography>
                  <Typography variant="body2">Strategic Goals</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%', bgcolor: '#2196f3', color: 'white' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>7</Typography>
                  <Typography variant="body2">Governance Areas</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%', bgcolor: '#ff9800', color: 'white' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>4</Typography>
                  <Typography variant="body2">Implementation Phases</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%', bgcolor: '#9c27b0', color: 'white' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>12</Typography>
                  <Typography variant="body2">Months Timeline</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>

        {/* Strategic Goals - Current State */}
        <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600, mt: 5, mb: 3 }}>
          Four Strategic Goals: Current State
        </Typography>

        <Grid container spacing={3} sx={{ mb: 5 }}>
          {currentFeatures.map((capability, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card sx={{ height: '100%', borderLeft: `4px solid ${capability.color}` }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ color: capability.color, mr: 2 }}>{capability.icon}</Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {capability.category}
                      </Typography>
                      <Chip
                        label={capability.status}
                        size="small"
                        sx={{
                          bgcolor: capability.color,
                          color: 'white',
                          fontWeight: 600,
                          mt: 0.5,
                        }}
                      />
                    </Box>
                  </Box>
                  <List dense>
                    {capability.features.map((feature, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon fontSize="small" sx={{ color: capability.color }} />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Unidatum-Inspired AI Governance Capabilities */}
        <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600, mt: 5, mb: 3 }}>
          Unidatum-Inspired AI Governance Capabilities
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Industry-Leading AI Governance</AlertTitle>
          Inspired by Unidatum's AI Governance platform, USCIS will implement comprehensive model lifecycle management, automated compliance, and universal oversight across all AI/ML platforms including Databricks, AWS, Azure, and Google Cloud.
        </Alert>

        <Grid container spacing={3} sx={{ mb: 5 }}>
          {unidatumCapabilities.map((capability, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card sx={{ height: '100%', borderLeft: `4px solid #2196f3` }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AIIcon sx={{ color: '#2196f3', mr: 2, fontSize: 32 }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#003366' }}>
                        {capability.category}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {capability.description}
                      </Typography>
                    </Box>
                  </Box>
                  <List dense>
                    {capability.features.map((feature, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon fontSize="small" sx={{ color: '#2196f3' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={feature}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {capability.platforms.map((platform, idx) => (
                      <Chip 
                        key={idx} 
                        label={platform} 
                        size="small" 
                        variant="outlined"
                        sx={{ borderColor: '#2196f3', color: '#2196f3' }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* AI Governance Framework */}
        <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600, mt: 5, mb: 3 }}>
          USCIS AI Governance Framework: Implementation Areas
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Governance Operationalization</AlertTitle>
          Governance is operationalized through task categories and objectives focused on data integrity controls, standardization, quality enforcement, root cause analysis, interoperable metadata frameworks, and automated governance approaches for AI-enabled systems.
        </Alert>

        <TableContainer component={Paper} sx={{ mb: 5 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#003366' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Governance Area</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Priority</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Key Features</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Effort</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Value</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Owner</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Unidatum Alignment</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {aiGovernanceEnhancements.map((enhancement, index) => (
                <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#f5f5f5' } }}>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {enhancement.category}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={enhancement.priority}
                      size="small"
                      color={
                        enhancement.priority === 'Critical'
                          ? 'error'
                          : enhancement.priority === 'High'
                          ? 'warning'
                          : 'default'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <List dense sx={{ py: 0 }}>
                      {enhancement.features.slice(0, 3).map((feature, idx) => (
                        <ListItem key={idx} sx={{ py: 0, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}>
                            <RadioButtonUncheckedIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                      {enhancement.features.length > 3 && (
                        <Typography variant="caption" color="text.secondary" sx={{ pl: 3 }}>
                          +{enhancement.features.length - 3} more features
                        </Typography>
                      )}
                    </List>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={enhancement.effort}
                      size="small"
                      variant="outlined"
                      color={
                        enhancement.effort === 'Large'
                          ? 'error'
                          : enhancement.effort === 'Medium'
                          ? 'warning'
                          : 'success'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={enhancement.value}
                      size="small"
                      sx={{
                        bgcolor:
                          enhancement.value === 'Very High'
                            ? '#4caf50'
                            : enhancement.value === 'High'
                            ? '#2196f3'
                            : '#ff9800',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {enhancement.owner}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ color: '#2196f3', fontWeight: 500 }}>
                      {enhancement.unidatumAlignment}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Governance Roles & Structure */}
        <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600, mt: 5, mb: 3 }}>
          Governance Roles & Structure
        </Typography>

        <Paper elevation={2} sx={{ p: 3, mb: 5, bgcolor: '#f5f5f5' }}>
          <Typography variant="body1" paragraph>
            OCDO, supported by the Data Sharing and Governance Branch, manages the Data Governance Framework, enforcing policies and facilitating the Data Management Council for strategic oversight of data and AI governance. Advanced Analytics Branch and Business Intelligence Branch enable continuous tech evaluation, process automation, and dashboarding—all with governance protocols built-in to guide responsible use of advanced analytic and AI technologies.
          </Typography>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {governanceRoles.map((role, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <VerifiedUserIcon sx={{ color: '#003366', mr: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#003366', fontSize: '1rem' }}>
                        {role.role}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {role.responsibility}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {role.activities.map((activity, idx) => (
                        <Chip key={idx} label={activity} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Implementation Roadmap */}
        <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600, mt: 5, mb: 3 }}>
          Implementation Roadmap: 12-Month Plan
        </Typography>

        <Grid container spacing={3} sx={{ mb: 5 }}>
          {roadmapPhases.map((phase, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card
                sx={{
                  height: '100%',
                  borderLeft: `6px solid ${
                    index === 0
                      ? '#4caf50'
                      : index === 1
                      ? '#2196f3'
                      : index === 2
                      ? '#ff9800'
                      : '#9c27b0'
                  }`,
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#003366' }}>
                        {phase.phase}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Focus: {phase.focus}
                      </Typography>
                    </Box>
                    <Chip
                      label={phase.timeline}
                      sx={{
                        bgcolor:
                          index === 0
                            ? '#4caf50'
                            : index === 1
                            ? '#2196f3'
                            : index === 2
                            ? '#ff9800'
                            : '#9c27b0',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Key Activities:
                  </Typography>
                  <List dense>
                    {phase.items.map((item, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <TrendingUpIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={item}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Key Milestones:
                  </Typography>
                  <List dense>
                    {phase.milestones.map((milestone, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon fontSize="small" sx={{ color: '#4caf50' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={milestone}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Responsible AI Practices */}
        <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: '#f1f8e9', border: '1px solid #aed581' }}>
          <Typography variant="h5" gutterBottom sx={{ color: '#558b2f', fontWeight: 600 }}>
            Responsible AI Practices & Risk Management
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <VisibilityIcon sx={{ fontSize: 40, color: '#558b2f', mr: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#558b2f' }}>
                      Transparency
                    </Typography>
                  </Box>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="Traceable AI outputs and decisions" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Explainable model documentation" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Regular model reviews and audits" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ShieldIcon sx={{ fontSize: 40, color: '#558b2f', mr: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#558b2f' }}>
                      Risk Controls
                    </Typography>
                  </Box>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="FICO-like risk scoring governance" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Automated routing oversight" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Agency risk policy alignment" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PsychologyIcon sx={{ fontSize: 40, color: '#558b2f', mr: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#558b2f' }}>
                      Data Literacy
                    </Typography>
                  </Box>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="Ethical AI usage training" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Explainable model education" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Process documentation standards" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>

        {/* Platform Integration & Technology Stack */}
        <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600, mt: 5, mb: 3 }}>
          Platform Integration & Technology Stack
        </Typography>

        <Paper elevation={2} sx={{ p: 3, mb: 5, bgcolor: '#e3f2fd' }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 600 }}>
            Universal AI/ML Platform Support
          </Typography>
          <Typography variant="body1" paragraph>
            USCIS AI Governance will support major AI/ML platforms for centralized oversight regardless of technology stack, ensuring consistent governance across all AI initiatives.
          </Typography>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ bgcolor: '#fff', textAlign: 'center', p: 2 }}>
                <IntegrationIcon sx={{ fontSize: 40, color: '#ff6f00', mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Databricks</Typography>
                <Typography variant="caption" color="text.secondary">AI/ML Platform</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ bgcolor: '#fff', textAlign: 'center', p: 2 }}>
                <IntegrationIcon sx={{ fontSize: 40, color: '#ff9900', mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>AWS</Typography>
                <Typography variant="caption" color="text.secondary">SageMaker & Bedrock</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ bgcolor: '#fff', textAlign: 'center', p: 2 }}>
                <IntegrationIcon sx={{ fontSize: 40, color: '#0078d4', mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Azure</Typography>
                <Typography variant="caption" color="text.secondary">Azure AI & OpenAI</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ bgcolor: '#fff', textAlign: 'center', p: 2 }}>
                <IntegrationIcon sx={{ fontSize: 40, color: '#4285f4', mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Google Cloud</Typography>
                <Typography variant="caption" color="text.secondary">Vertex AI</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ bgcolor: '#fff', textAlign: 'center', p: 2, border: '2px solid #003366' }}>
                <VerifiedUserIcon sx={{ fontSize: 40, color: '#003366', mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#003366' }}>E-Unify</Typography>
                <Typography variant="caption" color="text.secondary">USCIS Custom Platform</Typography>
              </Card>
            </Grid>
          </Grid>
        </Paper>

        {/* Key Benefits */}
        <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600, mt: 5, mb: 3 }}>
          Expected Benefits & Outcomes
        </Typography>
        
        <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: '#e8f5e9', border: '2px solid #4caf50' }}>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', bgcolor: '#fff' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <GavelIcon sx={{ fontSize: 40, color: '#2e7d32', mr: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                      Compliance
                    </Typography>
                  </Box>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="OMB AI guidance compliance" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="NIST AI Risk Management Framework" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Federal AI regulations adherence" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', bgcolor: '#fff' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SecurityIcon sx={{ fontSize: 40, color: '#2e7d32', mr: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                      Trust & Safety
                    </Typography>
                  </Box>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="Reduced bias in AI decisions" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Enhanced stakeholder trust" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Improved decision accuracy" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', bgcolor: '#fff' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SpeedIcon sx={{ fontSize: 40, color: '#2e7d32', mr: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                      Efficiency
                    </Typography>
                  </Box>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="Faster case processing" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Optimized resource allocation" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Reduced manual oversight" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>

        {/* Next Steps */}
        <Paper elevation={2} sx={{ p: 4, bgcolor: '#fff3e0', border: '2px solid #ff9800' }}>
          <Typography variant="h5" gutterBottom sx={{ color: '#ff9800', fontWeight: 600 }}>
            Recommended Next Steps
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: '#ff9800' }} />
              </ListItemIcon>
              <ListItemText
                primary="1. Establish AI Governance Council"
                secondary="Form cross-functional governance council with OCDO leadership, Advanced Analytics, and stakeholder representation"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: '#ff9800' }} />
              </ListItemIcon>
              <ListItemText
                primary="2. Conduct AI System Inventory"
                secondary="Complete comprehensive inventory of all AI/ML systems, models, and algorithms currently in use or development"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: '#ff9800' }} />
              </ListItemIcon>
              <ListItemText
                primary="3. Define Responsible AI Principles"
                secondary="Develop USCIS-specific responsible AI principles aligned with federal guidance and agency mission"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: '#ff9800' }} />
              </ListItemIcon>
              <ListItemText
                primary="4. Implement Risk Assessment Framework"
                secondary="Deploy AI risk assessment framework for all existing and new AI systems with clear risk scoring methodology"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: '#ff9800' }} />
              </ListItemIcon>
              <ListItemText
                primary="5. Launch Training Program"
                secondary="Roll out comprehensive AI literacy and ethics training for all staff working with or impacted by AI systems"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: '#ff9800' }} />
              </ListItemIcon>
              <ListItemText
                primary="6. Establish Metrics & KPIs"
                secondary="Define success metrics for AI governance including bias detection rates, model performance, and compliance scores"
              />
            </ListItem>
          </List>
        </Paper>

        {/* Footer */}
        <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            USCIS Office of the Chief Data Officer (OCDO) | AI Governance Roadmap
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Document Date: October 8, 2025 | Version 1.0 | Based on USCIS-DSSS-3-PWS
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default AIGovernanceRoadmap;
